/**
 * A capsule's cover film, on the web, and the hand-over to its questions.
 *
 * A capsule is a film and then its questions, and this is the half of that
 * sentence the module player was missing: the film. It is deliberately *only* the
 * film — it plays, it is watched, and when it ends it says so — because what
 * happens next differs between the places it is used (the feed's capsule player
 * hands over to the questions on the same screen; a module hands over and then
 * moves to the film of the next capsule in the playlist).
 *
 * ## Why this is a second implementation of the same idea
 *
 * `screens/CapsulePlay` already plays a capsule, and its film handling is real but
 * inseparable from the rest of that screen: the same video element is shared with
 * its own Play/Replay controls, its window, its focus handling and the capsule
 * queue it swipes through. What a *module* needs is narrower and platform-split
 * (`VideoItem` itself is split the same way), so the shared thing is the film and
 * its ending, and both screens can move onto it without one dragging the other's
 * controls along.
 *
 * ## The window
 *
 * A creator may have chosen a minute of a long film to open on (`coverStartMs`,
 * see `#/lib/coverTrim`). The film is uploaded whole, so the app seeks to that
 * minute once it is really playing and hands over at the end of the promised
 * window rather than at the end of the file. A film that already fits has no
 * window and simply plays to its end — which is every capsule that was not trimmed
 * by hand.
 */
import {useEffect, useMemo, useRef} from 'react'
import {StyleSheet, View} from 'react-native'
import {Gesture} from 'react-native-gesture-handler'
import {
  type AppBskyEmbedVideo,
  type AppBskyFeedDefs,
} from '@atproto/api'

import {VideoItem} from '#/screens/VideoFeed'

export function CapsuleFilmPlayer({
  post,
  video,
  /** Which millisecond of the film to open on; `0` for *from the start*. */
  startMs = 0,
  /** How long the film is allowed to run from there, in ms. `0` is *to its end*. */
  windowMs = 0,
  onFilmEnd,
  testID,
}: {
  post: AppBskyFeedDefs.PostView
  video: AppBskyEmbedVideo.View
  startMs?: number
  windowMs?: number
  /** The film is over — its questions are next. */
  onFilmEnd: () => void
  testID?: string
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  /*
   * The callback, read through a ref.
   *
   * The listeners below are installed once per film and must never close over a
   * stale `onFilmEnd`: handing over twice — once from `ended` and once from the
   * window's own edge — is the shape of bug that shows a learner the questions and
   * then moves the module on under them.
   */
  const doneRef = useRef(false)
  const onEndRef = useRef(onFilmEnd)
  onEndRef.current = onFilmEnd
  const startS = startMs > 0 ? startMs / 1000 : 0
  const endS = startS > 0 && windowMs > 0 ? startS + windowMs / 1000 : 0

  /*
   * The feed item takes a scroll gesture and this film is not swipeable, so there
   * is nothing to hand it. The prop is declared with the *native* type (the import
   * resolves to `screens/VideoFeed/index.tsx` at type level), which is why the null
   * is cast the way `CapsulePlay` casts one — and why the request is guarded:
   * webpack aliases `react-native-gesture-handler` to `false` on web, so
   * `Gesture.Native()` would be `undefined.Native()` there.
   */
  const scrollGesture = useMemo(
    () =>
      (Gesture?.Native ? Gesture.Native() : null) as ReturnType<
        typeof Gesture.Native
      >,
    [],
  )

  useEffect(() => {
    doneRef.current = false
    const video = videoRef.current
    if (!video) return

    const handOver = () => {
      if (doneRef.current) return
      doneRef.current = true
      video.pause()
      onEndRef.current()
    }

    const handleTimeUpdate = () => {
      if (doneRef.current) return
      if (endS > 0) {
        // Open on the chosen minute, once there is something to seek in.
        if (video.currentTime > 0 && video.currentTime < startS - 0.5) {
          video.currentTime = startS
          return
        }
        if (video.currentTime >= endS) {
          handOver()
        }
      }
    }

    video.addEventListener('ended', handOver)
    video.addEventListener('timeupdate', handleTimeUpdate)
    return () => {
      video.removeEventListener('ended', handOver)
      video.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [video.playlist, endS, startS])

  return (
    <View testID={testID} style={[StyleSheet.absoluteFill, styles.black]}>
      <VideoItem
        post={post}
        embed={video}
        active
        adjacent={false}
        scrollGesture={scrollGesture}
        /*
         * Declared required-but-undefined by the feed item's own type (a feed
         * always has both); a module's film came from nowhere in particular and
         * has neither, which is what `undefined` says.
         */
        feedContext={undefined}
        reqId={undefined}
        videoRef={videoRef}
        shouldPlay
      />
    </View>
  )
}

const styles = StyleSheet.create({
  black: {backgroundColor: 'black'},
})
